/**
 * SectionHeader
 * @module components/SectionHeader
 */

import clsx from 'clsx';
import {
	GenericWarning,
	renderElementBasedOnEditMode,
	ServerError,
	t,
} from 'common/helpers';
import CustomExternalScript from 'components/framework/CustomExternalScript';
import RichText from 'components/ui/RichText';
import React from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { UpdateCart } from 'store/modules/cart';
import { FastCheckout } from 'types/cart-types';
import { ContentButton } from './SectionHeader.styles';
import { SectionHeaderWrapper } from './SectionHeader.styles';
import { useSiteType } from 'context/siteType.context';
import Layout from '../Layout';
import Icon from '../Icon';

interface Sectionheader {
	title?: string;
	fastCheckout?: FastCheckout;
	heading: string;
	headingLevel?: string;
	text?: string;
	size: 'small' | 'large' | 'default';
	isInEditMode?: boolean;
	fullWidth?: boolean;
	className?: string;
}

const SectionHeader = (props: Sectionheader) => {
	const {
		heading,
		text,
		fastCheckout,
		size,
		isInEditMode,
		headingLevel,
		fullWidth = true,
		className,
	} = props;
	const dispatch = useDispatch();
	const siteType = useSiteType();
	const handleFastCheckoutChange = async () => {
		try {
			dispatch(
				UpdateCart({
					selectedFastCheckout: fastCheckout?.active,
				})
			);
		} catch (err) {
			if (err instanceof ServerError) {
				toast(t('shared/toast/servererror'), { type: 'error' });
			} else if (err instanceof GenericWarning) {
				toast(err.message, { type: 'warning' });
			}
		}
	};

	return (
		<Layout
			className={clsx(
				className,
				'mb-4 w-full',
				siteType === 'LindabGroup' && 'bg-lgLightGrayBlue'
			)}
		>
			<SectionHeaderWrapper
				as="section"
				className={clsx(!fullWidth && 'lg:w-8/12')}
			>
				<div className="flex w-full flex-col md:flex-row justify-between">
					<div className="flex flex-col">
						{heading &&
							renderElementBasedOnEditMode(
								isInEditMode || false,
								'heading',
								'h1',
								{
									className: `font-heading mb-6 mt-6 text-h2 md:text-h1 ${
										siteType === 'LindabGroup'
											? 'text-lgDarkBlue font-normal'
											: 'text-blue font-bold'
									}`,
									as: headingLevel,
								},
								heading
							)}

						<div className="max-w-full sm:max-w-2/3">
							<RichText
								name="text"
								text={text}
								size={size}
								isInEditMode={isInEditMode}
								className={clsx(
									siteType === 'LindabGroup' ? 'text-lgDarkGray' : 'text-black'
								)}
							/>
						</div>
					</div>
					{fastCheckout && (
						<div className="inline-block bg-greyDark p-4 md:ml-8 md:max-w-3xl relative max-w-full rounded-card">
							<RichText
								name="fastChekout text"
								text={fastCheckout?.text}
								isInEditMode={isInEditMode}
							/>
							<ContentButton
								buttonColor={fastCheckout?.active ? 'blue' : 'white'}
								onClick={() => handleFastCheckoutChange()}
								type="button"
							>
								<div className="flex items-center justify-around gap-3">
									{fastCheckout?.active && (
										<Icon icon="arrow" direction="left" size={1.25} />
									)}
									<span className="truncate overflow-ellipsis max-w-75">
										{fastCheckout.buttonText}
									</span>
									{!fastCheckout?.active && (
										<Icon icon="arrow" direction="right" size={1.25} />
									)}
								</div>
							</ContentButton>
						</div>
					)}
				</div>
			</SectionHeaderWrapper>
			<CustomExternalScript />
		</Layout>
	);
};

export default SectionHeader;
